/**
 * Week and date helpers. Everything works on `YYYY-MM-DD` strings and UTC so results do not
 * depend on the phone's or the server's time zone. Estimation weeks run Monday to Saturday.
 */
import { DAYS_PER_ESTIMATE } from '../schemas/common.js';
const DAY_MS = 86_400_000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export function parseIsoDate(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m)
        throw new Error(`Not a YYYY-MM-DD date: ${iso}`);
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    if (Number.isNaN(d.getTime()))
        throw new Error(`Not a valid date: ${iso}`);
    return d;
}
export function toIsoDate(d) {
    return d.toISOString().slice(0, 10);
}
export function addDays(iso, days) {
    return toIsoDate(new Date(parseIsoDate(iso).getTime() + days * DAY_MS));
}
/** The Monday on or before the given date. */
export function mondayOf(iso) {
    const d = parseIsoDate(iso);
    const dow = d.getUTCDay(); // 0 = Sunday
    const back = dow === 0 ? 6 : dow - 1;
    return addDays(iso, -back);
}
export function isMonday(iso) {
    return parseIsoDate(iso).getUTCDay() === 1;
}
/** ISO-8601 week number (weeks start Monday; week 1 contains the year's first Thursday). */
export function isoWeekNumber(iso) {
    const d = parseIsoDate(iso);
    const dow = d.getUTCDay() || 7; // Monday=1 .. Sunday=7
    d.setUTCDate(d.getUTCDate() + 4 - dow); // shift to the Thursday of this week
    const yearStart = Date.UTC(d.getUTCFullYear(), 0, 1);
    return Math.ceil(((d.getTime() - yearStart) / DAY_MS + 1) / 7);
}
/** The six estimation days for a week, Monday to Saturday. */
export function weekDays(weekStart) {
    return Array.from({ length: DAYS_PER_ESTIMATE }, (_, i) => addDays(weekStart, i));
}
/** Calendar week that contains today, as an estimation week. */
export function currentWeek(today) {
    const weekStart = mondayOf(today);
    return { weekNumber: isoWeekNumber(weekStart), weekStart };
}
/** The Thursday on or before the given date. */
function thursdayOnOrBefore(iso) {
    const dow = parseIsoDate(iso).getUTCDay(); // 0 = Sunday, 4 = Thursday
    return addDays(iso, -((dow - 4 + 7) % 7));
}
function windowOpeningOn(thursday) {
    const weekStart = addDays(mondayOf(thursday), 7);
    return {
        weekNumber: isoWeekNumber(weekStart),
        weekStart,
        opensOn: thursday,
        closesOn: addDays(thursday, 6),
    };
}
/** The estimate week growers may capture or edit on the given date. */
export function openEstimateWeek(today) {
    return windowOpeningOn(thursdayOnOrBefore(today));
}
/** The estimate week before the one open on the given date. */
export function previousEstimateWeek(today) {
    return windowOpeningOn(addDays(thursdayOnOrBefore(today), -7));
}
/* ---------- formatting, matching the design exactly ---------- */
/** `7 Sep 2026` */
export function formatDate(iso) {
    const d = parseIsoDate(iso);
    return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}
/** `Mon 7 Sep 2026` (day rows on the entry screen). */
export function formatDayLabel(iso) {
    const d = parseIsoDate(iso);
    const dow = d.getUTCDay();
    const short = dow === 0 ? 'Sun' : DAY_SHORT[dow - 1];
    return `${short} ${formatDate(iso)}`;
}
/** `Monday` .. `Saturday` for index 0..5. */
export function dayName(index) {
    const name = DAY_NAMES[index];
    if (!name)
        throw new RangeError(`Day index out of range: ${index}`);
    return name;
}
/**
 * `7–12 Sep 2026` when the week sits in one month, `31 Aug – 5 Sep 2026` when it straddles two,
 * `29 Dec 2025 – 3 Jan 2026` when it straddles a year. En dash, spaces only when months differ.
 */
export function formatWeekRange(weekStart) {
    return formatDateRange(weekStart, addDays(weekStart, DAYS_PER_ESTIMATE - 1));
}
/** Same rules as formatWeekRange for any two dates: `25 Jan – 20 Feb 2027`. */
export function formatDateRange(from, to) {
    const start = parseIsoDate(from);
    const end = parseIsoDate(to);
    const sameMonth = start.getUTCMonth() === end.getUTCMonth() && start.getUTCFullYear() === end.getUTCFullYear();
    if (sameMonth) {
        return `${start.getUTCDate()}–${end.getUTCDate()} ${MONTHS[end.getUTCMonth()]} ${end.getUTCFullYear()}`;
    }
    const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
    const left = sameYear ? `${start.getUTCDate()} ${MONTHS[start.getUTCMonth()]}` : formatDate(from);
    return `${left} – ${formatDate(toIsoDate(end))}`;
}
/**
 * Every week of a season in season order (week 28 of the first year through week 26 of the
 * next, for example). `startDate` is the season's first Monday, `endDate` its last Saturday.
 */
export function seasonWeeks(startDate, endDate) {
    const out = [];
    for (let ws = mondayOf(startDate); ws <= endDate; ws = addDays(ws, 7)) {
        out.push({ weekNumber: isoWeekNumber(ws), weekStart: ws });
    }
    return out;
}
/** The Monday of week `weekNumber` inside the season, or null when the season has no such week. */
export function seasonWeekStart(season, weekNumber) {
    return (seasonWeeks(season.startDate, season.endDate).find((w) => w.weekNumber === weekNumber)
        ?.weekStart ?? null);
}
/**
 * Runs of consecutive weeks for display, given week numbers already in season order:
 * [52, 53, 1, 2, 5] reads `52–53, 1–2, 5`.
 */
export function weekRuns(weeks) {
    const runs = [];
    for (const w of weeks) {
        const last = runs[runs.length - 1];
        if (last && w === last[1] + 1)
            last[1] = w;
        else
            runs.push([w, w]);
    }
    return runs.map(([a, b]) => (a === b ? String(a) : `${a}–${b}`)).join(', ');
}
//# sourceMappingURL=week.js.map